'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { FormFeedback } from '@ironhack/design-system2/components/form-feedback';
import { Text } from '@ironhack/design-system2/components/text';
import { useRouter } from 'next/navigation';
import { omit, pick } from 'ramda';
import { type ReactElement, useEffect, useState } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';
import { FormProvider, useForm } from 'react-hook-form';
import { useSessionStorage } from 'react-use';
import * as z from 'zod';

import { ContactDetailsFields } from '@/app/components/client/contact-details-fields';
import { updateUrlRegion } from '@/app/components/client/link';
import { PhoneField } from '@/app/components/client/phone-field';
import { SelectField } from '@/app/components/client/select-field';
import { StructuredContent } from '@/app/components/client/structured-content';
import { FormSubmit } from '@/app/components/server/form-submit';
import { ModuleWrapper } from '@/app/components/server/module-wrapper';
import { getPageUrl } from '@/app/urls/url-mapping';
import { useExperimentApplication } from '@/hooks/use-experiment-application';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import { useFormRequest } from '@/hooks/use-form-request';
import { useGtmLocalStorage } from '@/hooks/use-gtm-local-storage';
import { usePageContext } from '@/hooks/use-page-context';
import { useThankYouSession } from '@/hooks/use-thank-you-session';
import { type DatoTimeZone, getCopyForTimeZone } from '@/lib/datocms/dates';
import { type DatoRoute } from '@/lib/datocms/types';
import { sendEvent } from '@/lib/gtm';
import { TurnstileFormToken } from '@/lib/turnstile/components';
import {
  commonKeys,
  getCohortCardIconList,
  regionToCampusMap,
} from '@/lib/utils';
import { type RemoteTimezone } from '@/lib/utils/dates';
import { hackCohortFor20Weeks } from '@/lib/utils/experiments';
import { type ValidationMessageSet } from '@/lib/utils/form-validations';
import {
  type EmailInput,
  getEmailSchema,
} from '@/lib/utils/form-validations/email-schema';
import {
  type NameInputs,
  type NameValidationMessages,
  getNameSchema,
} from '@/lib/utils/form-validations/name-schema';
import {
  type PhoneInputs,
  getPhoneDefaults,
  getPhoneSchema,
} from '@/lib/utils/form-validations/phone-schema';
import { isMastersActive } from '@/lib/utils/masters';
import { type WebCohort } from '@/types';

import type { PageQuery } from '../../apply.queries';

type FormData = {
  cToken: string;
  newsletter: boolean;
  phone: PhoneInputs;
  referral: string;
} & EmailInput &
  NameInputs;

export type ApplyForm = {
  buttonText: string;
  chooseBetween: string;
  completeFields: string;
  emailLabel: string;
  emailPlaceholder: string;
  emailValidationMessages: ValidationMessageSet;
  errorMessage: string;
  firstNameLabel: string;
  firstNamePlaceholder: string;
  firstNameValidationMessages: NameValidationMessages;
  lastNameLabel: string;
  lastNamePlaceholder: string;
  lastNameValidationMessages: NameValidationMessages;
  legalText: StructuredTextGraphQlResponse;
  newsletterLabel: string;
  phoneInvalidMessage: string;
  phoneLabel: string;
  phonePlaceholder: string;
  phoneRequiredMessage: string;
  phoneStep2Text: string;
  referralLabel: string;
  referralOptions: Array<{ key: string; text: string }>;
  referralPlaceholder: string;
  step2Subtitle: string;
  step2Title: string;
  title: string;
};

export type Props = {
  data: {
    applyForm: ApplyForm;
    thankYouPage: DatoRoute;
    timeZones: DatoTimeZone[];
  };
  cohort: WebCohort;
};

type PropsForm = {
  applyForm: ApplyForm;
  cohort: WebCohort;
  onStep2: () => void;
  onSuccess: () => void;
} & FlexProps;

const getSchema = (messages: ApplyForm) =>
  z
    .object({
      cToken: z.string().min(1, messages.errorMessage),
      newsletter: z.boolean(),
      phone: getPhoneSchema(messages),
      referral: z.string(),
    })
    .merge(getEmailSchema(messages.emailValidationMessages))
    .merge(
      getNameSchema(
        messages.firstNameValidationMessages,
        messages.lastNameValidationMessages
      )
    )
    .required();

export const Form = (props: PropsForm): ReactElement => {
  const { applyForm, cohort, onSuccess, onStep2, ...flexProps } = props;
  const {
    buttonText,
    errorMessage,
    legalText,
    newsletterLabel,
    phoneLabel,
    phonePlaceholder,
    referralLabel,
    referralOptions,
    referralPlaceholder,
  } = applyForm;
  const {
    params: { language, region },
  } = usePageContext();
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [showReferral, setShowReferral] = useState(true);

  const formSchema = getSchema(applyForm);

  const formMethods = useForm<FormData>({
    defaultValues: {
      phone: getPhoneDefaults(region),
      newsletter: false,
      referral: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    clearErrors,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    setValue,
    trigger,
  } = formMethods;
  const [formFeedback, setFormFeedback] = useState<{
    status: 'error';
    message: string;
  } | null>();
  const { isEnabled } = useFeatureToggle();
  const formRequest = useFormRequest();
  const [campus] = useSessionStorage('campus', regionToCampusMap[region][0]);
  const [, setGtmLocalStorage] = useGtmLocalStorage();
  const [, setThankYouSession] = useThankYouSession();
  const { experimentId, salesforceId, variantId } = useExperimentApplication(
    'valueProp',
    cohort.external_ids.salesforce
  );
  const setCaptchaToken = (token: string) => setValue('cToken', token);

  const onSubmit = async (formData: FormData): Promise<void> => {
    setFormFeedback(null);
    try {
      const response = await formRequest({
        data: {
          ...omit(['newsletter'], formData),
          courseId: salesforceId,
          emailOptIn: formData.newsletter,
          language,
          websiteCampusPage: campus,
        },
        experimentId,
        formType: 'apply',
        variantId,
      });

      sendEvent({
        campus: cohort.campus,
        course: cohort.track,
        email: formData.email,
        eventAction: 'send cohort application',
        eventCategory: 'PDP',
        eventLabel: `${cohort.campus}::${cohort.track}::${cohort.format}`,
        format: cohort.format,
        pageType: 'PDP',
      });
      const {
        firstName,
        phone: { phoneNumber, country },
      } = formData;
      setGtmLocalStorage({
        applicationData: {
          campus: cohort.campus,
          course: cohort.track,
          format: cohort.format,
          phoneNumber,
          ...pick(['email', 'firstName', 'lastName'], formData),
        },
        countryName: country,
      });

      if (isEnabled('dynamic-thank-you')) {
        await setThankYouSession({ firstName, response });
      }
      onSuccess();
    } catch {
      setFormFeedback({ status: 'error', message: errorMessage });
    }
  };

  return (
    <Flex direction="column" {...flexProps}>
      <FormProvider {...formMethods}>
        <form id="cohortApplyForm" noValidate onSubmit={handleSubmit(onSubmit)}>
          <Flex alignItems="center" direction="column">
            {step === 1 && (
              <ContactDetailsFields
                fields={pick(
                  [
                    'chooseBetween',
                    'completeFields',
                    'emailLabel',
                    'emailPlaceholder',
                    'firstNameLabel',
                    'firstNamePlaceholder',
                    'lastNameLabel',
                    'lastNamePlaceholder',
                    'phoneLabel',
                    'phonePlaceholder',
                  ],
                  applyForm
                )}
                onGoogleClick={() => {
                  setFormFeedback(null);
                  setIsGoogleSubmitting(true);
                }}
                onGoogleError={() => {
                  setIsGoogleSubmitting(false);
                  setFormFeedback({
                    status: 'error',
                    message: errorMessage,
                  });
                }}
                onGoogleSuccess={async (googleData) => {
                  setIsGoogleSubmitting(false);
                  setValue('email', googleData.email);
                  setValue('firstName', googleData.firstName);
                  setValue('lastName', googleData.lastName);
                  const isValid = await trigger([
                    'email',
                    'firstName',
                    'lastName',
                  ]);
                  if (!isValid) return;

                  clearErrors();
                  if (getValues('referral')) {
                    setShowReferral(false);
                  }
                  setStep(2);
                  onStep2();
                }}
                setFormValues={false}
                withPhone
              />
            )}
            {step === 2 && (
              <PhoneField
                error={
                  errors.phone?.message || errors.phone?.phoneNumber?.message
                }
                isRequired
                label={phoneLabel}
                name="phone"
                placeholder={phonePlaceholder}
              />
            )}
            {showReferral && (
              <SelectField
                isRequired={false}
                label={referralLabel}
                mt={2}
                name="referral"
                options={referralOptions}
                placeholder={referralPlaceholder}
              />
            )}

            <TurnstileFormToken
              language={language}
              onVerify={setCaptchaToken}
            />
            <FormSubmit
              buttonText={buttonText}
              isLoading={isSubmitting || isGoogleSubmitting}
              legalText={legalText}
              newsletterLabel={newsletterLabel}
              withNewsletter
            />
            {formFeedback?.status && <FormFeedback mt={2} {...formFeedback} />}
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  );
};

export const ApplyForm = (props: Props): ReactElement => {
  const {
    data: { applyForm, thankYouPage, timeZones },
  } = props;
  const { title, step2Subtitle, step2Title } = applyForm;
  const [isStep2, setIsStep2] = useState(false);
  const router = useRouter();
  const { params, locale } = usePageContext<PageQuery>();
  const { isEnabled } = useFeatureToggle();

  const { language, region } = params;
  let { cohort } = props;
  const { isNewFormat } = useExperimentApplication(
    'valueProp',
    cohort.external_ids.salesforce
  );
  if (isNewFormat) {
    cohort = hackCohortFor20Weeks(cohort);
  }

  // Don't show end date and number of weeks until client takes over
  // (it depends on session storage)
  const [showEndPeriod, setShowEndPeriod] = useState(false);
  useEffect(() => setShowEndPeriod(true), [setShowEndPeriod]);

  const timeZone = getCopyForTimeZone(
    cohort.timezone as RemoteTimezone,
    timeZones
  );
  const cohortIconList = getCohortCardIconList(cohort, locale, timeZone, {
    showEndPeriod,
    ...(isMastersActive(region) && { omitItems: ['format'] }),
  });

  const handleSuccess = () => {
    const url = isEnabled('dynamic-thank-you')
      ? getPageUrl('thankYouDynamic', params)
      : updateUrlRegion(thankYouPage.path);

    if (url) router.push(url);
  };

  return (
    <ModuleWrapper
      innerProps={{ direction: 'column', alignItems: 'center' }}
      moduleFormat={{
        background: 'light',
        spacingBottom: '40',
        spacingTop: '40',
      }}
      outerProps={{}}
    >
      <Flex
        bg={['background.light', null, 'background.white']}
        borderRadius="16px"
        direction="column"
        p={[0, null, 5]}
        w={['full', null, '680px']}
      >
        <Text as="h1" textStyle={['3xl', null, '5xl']}>
          {title}
        </Text>
        {!isStep2 && (
          <Flex
            bg="background.white"
            borderRadius="16px"
            boxShadow="0px 4px 8px rgba(0, 0, 0, 0.16)"
            direction="column"
            gap={1}
            id="apply-form-course-details"
            mt={[3, null, 6]}
            p={2}
            w={['full', null, '268px']}
          >
            <Text textStyle="2xl">{commonKeys[language][cohort.track]}</Text>
            <Text textStyle="xlBold">
              {commonKeys[language][cohort.campus]}
            </Text>
            <StructuredContent data={cohortIconList} textStyle="m" />
          </Flex>
        )}
        {isStep2 && (
          <>
            <Text mt={4} textStyle="lBold">
              {step2Title}
            </Text>
            <Text mt={2} textStyle="l">
              {step2Subtitle}
            </Text>
          </>
        )}
        <Form
          applyForm={applyForm}
          cohort={cohort}
          mt={4}
          onStep2={() => setIsStep2(true)}
          onSuccess={handleSuccess}
        />
      </Flex>
    </ModuleWrapper>
  );
};
